import { useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import {
  modelRegistryApi,
  registryErrorMessage,
} from "@/services/modelRegistryApi";
import {
  MODEL_STATES,
  type AdminModel,
  type ModelRoleName,
  type ModelState,
} from "@/types/modelRegistry";
import {
  ROLE_LABELS,
  STATE_LABELS,
  formatCost,
  formatRelative,
  formatTokens,
} from "./format";
import { Menu, type MenuItem } from "./Menu";
import { Btn, Help, Modal, Panel, Pill, Skeleton } from "./ui";

interface ModelsTableProps {
  models: AdminModel[] | null;
  onEdit: (model: AdminModel) => void;
  onChanged: () => Promise<void>;
}

type StateFilter = "all" | ModelState;

const matches = (model: AdminModel, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    model.name,
    model.slug,
    model.modelName,
    model.provider.name,
    model.provider.slug,
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
};

async function run(label: string, work: () => Promise<void>): Promise<void> {
  try {
    await work();
  } catch (error) {
    toast.error(`${label}: ${registryErrorMessage(error)}`);
  }
}

function useRowActions(
  model: AdminModel,
  onEdit: () => void,
  onChanged: () => Promise<void>,
) {
  const [busy, setBusy] = useState<"test" | "sync" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const test = () =>
    run("Test failed", async () => {
      setBusy("test");
      try {
        const result = await modelRegistryApi.testModel(model.id);
        const tokens =
          result.inputTokens !== null && result.outputTokens !== null
            ? ` · ${result.inputTokens} in / ${result.outputTokens} out`
            : "";
        toast.success(
          `${model.name} answered "${result.reply}" in ${result.latencyMs} ms${tokens}`,
        );
      } finally {
        setBusy(null);
      }
    });

  const sync = () =>
    run("Sync failed", async () => {
      setBusy("sync");
      try {
        await modelRegistryApi.syncModel(model.id);
        toast.success(`${model.name} refreshed from OpenRouter`);
        await onChanged();
      } finally {
        setBusy(null);
      }
    });

  const setState = (state: ModelState) =>
    run("Could not change state", async () => {
      await modelRegistryApi.updateModel(model.id, { state });
      await onChanged();
    });

  const setRole = (role: ModelRoleName) =>
    run("Could not set default", async () => {
      await modelRegistryApi.setRole(role, model.id);
      toast.success(`${ROLE_LABELS[role].label} now uses ${model.name}`);
      await onChanged();
    });

  const remove = () =>
    run("Delete failed", async () => {
      await modelRegistryApi.deleteModel(model.id);
      toast.success(`${model.name} deleted`);
      setConfirmDelete(false);
      await onChanged();
    });

  const copyId = () =>
    run("Copy failed", async () => {
      await navigator.clipboard.writeText(model.modelName);
      toast.success(`Copied ${model.modelName}`);
    });

  const menu: MenuItem[] = [
    {
      label: "Test model",
      hint: "One short call, shows reply and latency",
      onSelect: () => void test(),
    },
    ...(model.provider.slug === "openrouter"
      ? [
          {
            label: "Refresh from OpenRouter",
            hint: `Synced ${formatRelative(model.syncedAt).toLowerCase()}`,
            onSelect: () => void sync(),
          },
        ]
      : []),
    {
      label: "Copy vendor id",
      hint: model.modelName,
      onSelect: () => void copyId(),
    },
    ...(model.kind === "chat" && model.state !== "disabled"
      ? (Object.keys(ROLE_LABELS) as ModelRoleName[])
          .filter((role) => !model.roles.includes(role))
          .map((role) => ({
            label: `Use as ${ROLE_LABELS[role].label.toLowerCase()}`,
            onSelect: () => void setRole(role),
          }))
      : []),
    { label: "Edit", onSelect: onEdit },
    {
      label: "Delete",
      hint:
        model.roles.length > 0
          ? "Point its defaults elsewhere first"
          : "Gone for good",
      disabled: model.roles.length > 0,
      danger: true,
      onSelect: () => setConfirmDelete(true),
    },
  ];

  return { busy, menu, setState, confirmDelete, setConfirmDelete, remove };
}

const cell = "px-4 py-3 align-middle text-sm";
const num = `${cell} text-right tabular-nums text-gray-900`;

function ModelRow({
  model,
  onEdit,
  onChanged,
}: {
  model: AdminModel;
  onEdit: (model: AdminModel) => void;
  onChanged: () => Promise<void>;
}) {
  const { busy, menu, setState, confirmDelete, setConfirmDelete, remove } =
    useRowActions(model, () => onEdit(model), onChanged);
  const roleBound = model.roles.length > 0;
  return (
    <tr className="border-t border-gray-100 transition-colors hover:bg-gray-50/60">
      <td className={cell}>
        <button
          type="button"
          onClick={() => onEdit(model)}
          className="group flex flex-col items-start text-left"
        >
          <span className="font-medium text-gray-900 group-hover:underline">
            {model.name}
          </span>
          <span className="inline-flex items-center gap-1 font-mono text-xs text-gray-500">
            {model.modelName}
            {model.provider.slug === "openrouter" && model.syncedAt && (
              <span
                className="text-gray-400"
                title={`Vendor facts synced ${formatRelative(model.syncedAt).toLowerCase()}`}
              >
                · {formatRelative(model.syncedAt).toLowerCase()}
              </span>
            )}
          </span>
        </button>
      </td>
      <td className={`${cell} whitespace-nowrap text-gray-600`}>
        {model.provider.name}
      </td>
      <td className={cell}>
        <div className="flex flex-wrap gap-1">
          {model.input.map((modality) => (
            <Pill key={modality}>{modality}</Pill>
          ))}
        </div>
      </td>
      <td className={num}>{formatTokens(model.contextWindow)}</td>
      <td className={`${num} whitespace-nowrap`}>{formatCost(model.cost)}</td>
      <td className={cell}>
        <select
          aria-label={`State of ${model.name}`}
          value={model.state}
          onChange={(e) => void setState(e.target.value as ModelState)}
          title={
            roleBound
              ? "A default cannot be disabled. Point the default elsewhere first."
              : STATE_LABELS[model.state].hint
          }
          className="h-8 rounded-md border border-transparent bg-transparent pl-1 pr-6 text-sm text-gray-900 hover:border-gray-200 hover:bg-white focus:border-gray-300 focus:outline-none"
        >
          {MODEL_STATES.map((state) => (
            <option
              key={state}
              value={state}
              disabled={state === "disabled" && roleBound}
            >
              {STATE_LABELS[state].label}
            </option>
          ))}
        </select>
      </td>
      <td className={cell}>
        <div className="flex flex-wrap gap-1">
          {model.roles.map((role) => (
            <Pill key={role} tone="outline">
              {ROLE_LABELS[role].label}
            </Pill>
          ))}
        </div>
      </td>
      <td className={`${cell} text-right`}>
        <div className="flex items-center justify-end gap-1">
          {busy && (
            <RefreshCw
              className="size-3.5 animate-spin text-gray-400"
              aria-label={busy === "test" ? "Testing" : "Syncing"}
            />
          )}
          <Menu label={`Actions for ${model.name}`} items={menu} />
        </div>
        <Modal
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={`Delete ${model.name}?`}
          description="Users lose it from the picker at once. Past chats keep their record of which model answered."
          footer={
            <>
              <Btn variant="ghost" onClick={() => setConfirmDelete(false)}>
                Keep it
              </Btn>
              <Btn
                onClick={() => void remove()}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </Btn>
            </>
          }
        >
          <p className="text-sm text-gray-600">
            <span className="font-mono">{model.modelName}</span> on{" "}
            {model.provider.name}.
          </p>
        </Modal>
      </td>
    </tr>
  );
}

function Toolbar({
  models,
  query,
  onQuery,
  filter,
  onFilter,
  onChanged,
}: {
  models: AdminModel[];
  query: string;
  onQuery: (q: string) => void;
  filter: StateFilter;
  onFilter: (f: StateFilter) => void;
  onChanged: () => Promise<void>;
}) {
  const [syncing, setSyncing] = useState(false);
  const counts = useMemo(
    () =>
      MODEL_STATES.map((state) => ({
        state,
        n: models.filter((m) => m.state === state).length,
      })),
    [models],
  );
  const openRouterModels = models.filter(
    (m) => m.provider.slug === "openrouter",
  );

  const syncAll = () =>
    run("Sync failed", async () => {
      setSyncing(true);
      try {
        const results = await Promise.allSettled(
          openRouterModels.map((m) => modelRegistryApi.syncModel(m.id)),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        toast[failed ? "warning" : "success"](
          failed
            ? `${openRouterModels.length - failed} refreshed, ${failed} failed`
            : `${openRouterModels.length} models refreshed from OpenRouter`,
        );
        await onChanged();
      } finally {
        setSyncing(false);
      }
    });

  const chip = (value: StateFilter, label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => onFilter(value)}
      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${filter === value ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="relative flex-1 min-w-[220px]">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search by name, id or provider"
          className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-3 text-sm shadow-sm outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-200"
        />
      </label>
      <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white p-0.5">
        {chip("all", `All ${models.length}`)}
        {counts.map(({ state, n }) =>
          chip(state, `${STATE_LABELS[state].label} ${n}`),
        )}
      </div>
      {openRouterModels.length > 0 && (
        <Btn
          variant="outline"
          size="sm"
          onClick={() => void syncAll()}
          disabled={syncing}
        >
          <RefreshCw className={`size-3.5 ${syncing ? "animate-spin" : ""}`} />
          Sync all from OpenRouter
        </Btn>
      )}
    </div>
  );
}

const head = "px-4 py-2.5 text-left text-xs font-medium text-gray-500";

export function ModelsTable({ models, onEdit, onChanged }: ModelsTableProps) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<StateFilter>("all");
  if (!models) return <Skeleton className="h-64" />;
  const shown = models.filter(
    (m) => matches(m, query) && (filter === "all" || m.state === filter),
  );

  return (
    <div className="flex flex-col gap-3">
      <Toolbar
        models={models}
        query={query}
        onQuery={setQuery}
        filter={filter}
        onFilter={setFilter}
        onChanged={onChanged}
      />
      <Panel>
        <table className="w-full">
          <thead className="bg-gray-50/80">
            <tr>
              <th className={head}>Model</th>
              <th className={head}>Provider</th>
              <th className={head}>
                <span className="inline-flex items-center gap-1">
                  Reads
                  <Help
                    label="reads"
                    lines={[
                      ["text", "Plain text prompts."],
                      ["image", "Images go to the model as pixels."],
                      [
                        "pdf",
                        "PDFs go to the model as files, not extracted text.",
                      ],
                    ]}
                  />
                </span>
              </th>
              <th className={`${head} text-right`}>Context</th>
              <th className={`${head} text-right`}>$ in / out per 1M</th>
              <th className={head}>
                <span className="inline-flex items-center gap-1">
                  State
                  <Help
                    label="state"
                    lines={[
                      ["Active", "Usable, and shown in the chat picker."],
                      ["Hidden", "Usable by defaults only. Not in the picker."],
                      [
                        "Disabled",
                        "Not usable anywhere. A default cannot be disabled.",
                      ],
                    ]}
                  />
                </span>
              </th>
              <th className={head}>
                <span className="inline-flex items-center gap-1">
                  Used as
                  <Help
                    label="defaults"
                    lines={(Object.keys(ROLE_LABELS) as ModelRoleName[]).map(
                      (role) => [
                        ROLE_LABELS[role].label,
                        ROLE_LABELS[role].hint,
                      ],
                    )}
                  />
                </span>
              </th>
              <th className={head}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((model) => (
              <ModelRow
                key={model.id}
                model={model}
                onEdit={onEdit}
                onChanged={onChanged}
              />
            ))}
            {shown.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="py-10 text-center text-sm text-gray-500"
                >
                  {models.length === 0
                    ? "No models yet. Add one to start."
                    : "No model matches."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
